/// <reference lib="dom" />

import type {ImageGenerator, IMaterialData, MeshRenderSettings, TextureStreamingMode} from "@cm/material-nodes"
import type {CompleteMeshData} from "#template/interfaces/mesh-data"
import type {Matrix4, Vector3} from "@cm/math"
import {z} from "zod/v4-mini"
import type {TextureResolution, ToneMapping} from "#template/nodes/types"

import type {CompleteCurveData} from "#template/interfaces/curve-data"
import type {TemplateNode} from "#template/template-node/template-node"
import type {DataObject} from "@cm/utils"

//TODO: move this?
export namespace GlobalRenderConstants {
    //TODO: figure out the correct units to use, and how this was originally derived
    // 1 watt of a green 555nm light is 683 lumens
    // Luminous flux/power (Lumen): lm = cd*sr
    // Luminous intensity (Candela): cd = lm/sr (power of a point source)
    // Luminance: cd/m^-2 or lm/(sr*m^2) (Luminous flux per unit solid angle per unit projected source area)
    // ??? normalized to a diffuse white surface illuminated by a 100 watt point source 1 meter away ???
    export const lightIntensityScale = 6.83
}

export type ObjectId = string
export type ObjectPath = TemplateNode[]
export type MaterialSlot = number

export type Color = [number, number, number]
export type UiStyle = "default" | "icons" | "hidden" | "accordion" | "mobileTabs"
export type EnvironmentMapMode = "full" | "specularOnly"

export function colorEqual(a: Color, b: Color): boolean {
    if (!a && !b) {
        return true
    } else if (!b || !a) {
        return false
    } else if (a[0] !== b[0] || a[1] !== b[1] || a[2] !== b[2]) {
        return false
    } else {
        return true
    }
}

export type SurfacePointInfo = {
    objectId: ObjectId
    materialSlot: MaterialSlot
    triIndex: number
    objX: number
    objY: number
    objZ: number
    u: number
    v: number
    worldX: number
    worldY: number
    worldZ: number
    displayMeshToken: any
}

export type DimensionGuideEntry = {
    start: Vector3
    end: Vector3
    label: string
}

export type DimensionGuideInfo = DimensionGuideEntry | DimensionGuideEntry[]

export type EnvironmentImageData = {readonly type: "dataObject"; dataObject: DataObject} | {readonly type: "url"; url: string; originalFileExtension: string}

////////////////////////////////////////////////////////////////////////

export namespace SceneNodes {
    interface SceneNodeDef<T extends SceneNodeBase> {
        is(x: SceneNodeBase): x is T
    }

    // export interface Entity {
    //     get<T extends SceneNode>(def: SceneNodeDef<T>): T | undefined;
    // }

    interface SceneNodeBase {
        id: ObjectId
        path: ObjectPath
        readonly type: string
        selectable?: boolean
    }

    export const isSceneNode = (x: object): x is SceneNodeBase => x !== null && "type" in x && typeof x.type === "string"

    export function defSceneNode<T extends SceneNodeBase>(type: T["type"]): SceneNodeDef<T> {
        return {
            is: (x): x is T => x.type === type,
        }
    }

    // export const ObjectId = defSceneNode<ObjectId>('ObjectId');
    // export type ObjectId = {
    //     id: string;
    // }

    // function mkTypeTestFn<T extends SceneNode>(type: T['type']): (x: SceneNode) => x is T {
    //     return (x): x is T => x.type === type;
    // }

    // export const Hierarchy = defSceneNode<Hierarchy>('Hierarchy');
    // export interface Hierarchy extends SceneNode {
    //     type: 'Hierarchy';
    //     children: Entity[];
    // }

    // export const Transform = defSceneNode<Transform>('Transform');
    // export interface Transform extends SceneNode {
    //     type: 'Transform';
    //     matrix: Matrix4;
    //     isFirstTransformUpdate?: boolean;
    //     forceTransform?: Matrix4;
    //     lockedTransform?: number[];
    //     defaultTransform?: number[];
    // }

    export const Mesh = defSceneNode<Mesh>("Mesh")

    export interface Mesh extends SceneNodeBase {
        readonly type: "Mesh"
        completeMeshData: CompleteMeshData
        meshRenderSettings: MeshRenderSettings
        transform: Matrix4
        materialMap: Map<number, IMaterialData | null>
        visibleDirectly: boolean
        visibleInReflections: boolean
        visibleInRefractions: boolean
        receiveRealtimeShadows: boolean
        castRealtimeShadows: boolean
        isDecal: boolean
        isProcedural: boolean
        parent: SceneNodes.Mesh | null
    }

    export const MeshCurveControl = defSceneNode<MeshCurveControl>("MeshCurveControl")

    export interface MeshCurveControl extends SceneNodeBase {
        readonly type: "MeshCurveControl"
        transform: Matrix4
        controlPoints: {position: Vector3; corner: boolean}[]
        closed: boolean
        mesh: SceneNodes.Mesh
        completeCurveData: CompleteCurveData
    }

    export const Seam = defSceneNode<Seam>("Seam")
    export interface Seam extends SceneNodeBase {
        readonly type: "Seam"
        transform: Matrix4
        item: SceneNodes.Mesh[]
        meshCurveControl: MeshCurveControl
        completeCurveData: CompleteCurveData
    }

    export const DimensionGuides = defSceneNode<DimensionGuides>("DimensionGuides")

    export interface DimensionGuides extends SceneNodeBase {
        readonly type: "DimensionGuides"
        concealable: boolean
        guides: DimensionGuideInfo[]
    }

    export const WireframeMesh = defSceneNode<WireframeMesh>("WireframeMesh")

    export interface WireframeMesh extends SceneNodeBase {
        readonly type: "WireframeMesh"
        completeMeshData: CompleteMeshData
        transform: Matrix4
        channel: "faces" | "uv0" | "uv1" | "uv2"
    }

    export const Rectangle = defSceneNode<Rectangle>("Rectangle")

    export interface Rectangle extends SceneNodeBase {
        readonly type: "Rectangle"
        width: number
        height: number
        transform: Matrix4
    }

    export const Cuboid = defSceneNode<Cuboid>("Cuboid")

    export interface Cuboid extends SceneNodeBase {
        readonly type: "Cuboid"
        width: number
        height: number
        depth: number
        transform: Matrix4
    }

    export const Point = defSceneNode<Point>("Point")

    export interface Point extends SceneNodeBase {
        readonly type: "Point"
        size: number
        transform: Matrix4
    }

    export const SceneOptions = defSceneNode<SceneOptions>("SceneOptions")

    export interface SceneOptions extends SceneNodeBase {
        readonly type: "SceneOptions"
        templateDepth: number
        maxSubdivisionLevel: number
        maxSubdivisionLevelOnMobile?: number
        backgroundColor?: Color
        backgroundImage?: DataObject
        shiftAndZoomBackgroundImage: boolean
        loadingBackgroundImage?: DataObject
        uiColor: Color
        uiStyle: UiStyle
        useSectionedMenu: boolean
        iconSize: number
        enableAr: boolean
        enableSalesEnquiry: boolean
        textureResolution?: TextureResolution
        textureFiltering: boolean
        textureStreamingMode?: TextureStreamingMode
        enableRealtimeShadows: boolean
        enableRealtimeLights: boolean
        enableRealtimeMaterials: boolean
        realtimeShadowMapOptions?: {
            resolution: number
            outerUpdateIterations: number
            innerUpdateIterations: number
            outerSmoothIterations: number
            innerSmoothIterations: number
        }
        enableOnboardingHint: boolean
        enableGltfDownload: boolean
        enableStlDownload: boolean
        enableSnapshot: boolean
        enablePdfDownload: boolean
        enableFullscreen: boolean
        environmentMapMode: EnvironmentMapMode
        showAnnotations: boolean
        enableDimensionGuides: boolean
        shadowCatcherFalloff?: {
            sizeX: number
            sizeZ: number
            smoothness: number
            opacity: number
        }
        enableAdaptiveSubdivision: boolean
        verticalArPlacement: boolean
    }

    export const Environment = defSceneNode<Environment>("Environment")

    export interface Environment extends SceneNodeBase {
        readonly type: "Environment"
        rotation: Vector3
        intensity: number
        saturation: number
        clampHighlights?: number
        mirror: boolean
        envData: EnvironmentImageData
    }

    export const Camera = defSceneNode<Camera>("Camera")

    export interface Camera extends SceneNodeBase {
        readonly type: "Camera"
        templateDepth: number
        focalLength: number
        focalDistance: number
        resolutionX: number
        resolutionY: number
        filmGauge: number
        fStop: number
        exposure: number
        shiftX: number
        shiftY: number
        zoomFactor: number // Already applied to focalLength
        nearClip?: number
        farClip?: number
        transform: Matrix4
        autoFocus: boolean
        target: Vector3
        targeted: boolean
        realtimeSettings: {
            toneMapping: ToneMapping
            enableOrbiting: boolean
            minDistance?: number
            maxDistance?: number
            minPolarAngle?: number
            maxPolarAngle?: number
            minAzimuthAngle?: number
            maxAzimuthAngle?: number
            enablePanning: boolean
            screenSpacePanning: boolean
            adaptAspectRatio: boolean
            enableDamping: boolean
            dampingFactor: number
        }
    }

    export const AreaLight = defSceneNode<AreaLight>("AreaLight")

    export interface AreaLight extends SceneNodeBase {
        readonly type: "AreaLight"
        intensity: number
        on: boolean
        color: Color
        texture?: ImageGenerator
        width: number
        height: number
        directionality: number
        visibleDirectly: boolean
        visibleInReflections: boolean
        visibleInRefractions: boolean
        target: Vector3
        targeted: boolean
        transform: Matrix4
        transparent: boolean
    }

    export const PointLight = defSceneNode<PointLight>("PointLight")

    export interface PointLight extends SceneNodeBase {
        readonly type: "PointLight"
        intensity: number
        on: boolean
        color: Color
        radius: number
        visibleDirectly: boolean
        visibleInReflections: boolean
        visibleInRefractions: boolean
        transform: Matrix4
    }

    export const SunLight = defSceneNode<SunLight>("SunLight")

    export interface SunLight extends SceneNodeBase {
        readonly type: "SunLight"
        intensity: number
        on: boolean
        color: Color
        angle: number
        visibleDirectly: boolean
        visibleInReflections: boolean
        visibleInRefractions: boolean
        transform: Matrix4
    }

    export const LightPortal = defSceneNode<LightPortal>("LightPortal")

    export interface LightPortal extends SceneNodeBase {
        readonly type: "LightPortal"
        width: number
        height: number
        transform: Matrix4
    }

    export const Annotation = defSceneNode<Annotation>("Annotation")

    export interface Annotation extends SceneNodeBase {
        readonly type: "Annotation"
        transform: Matrix4
        label: string
        description: string
        annotationID?: string
    }

    export const PreloadMaterial = defSceneNode<PreloadMaterial>("PreloadMaterial")

    export interface PreloadMaterial extends SceneNodeBase {
        readonly type: "PreloadMaterial"
        materialData: IMaterialData
        meshRenderSettings: MeshRenderSettings | undefined
    }

    export const DataObjectReference = defSceneNode<DataObjectReference>("DataObjectReference")

    export interface DataObjectReference extends SceneNodeBase {
        readonly type: "DataObjectReference"
        dataObject: DataObject
        origin:
            | {
                  readonly type: "materialAssignment"
                  nodeId: string
                  final: boolean
                  slots: Set<string>
              }
            | {
                  readonly type: "mesh"
              }
            | {
                  readonly type: "sceneProperties"
              }
            | {
                  readonly type: "hdriLight"
              }
            | {
                  readonly type: "postProcessRender"
              }
    }

    export const Grid = defSceneNode<Grid>("Grid")

    export interface Grid extends SceneNodeBase {
        readonly type: "Grid"
        size: number
        divisions: number
        color1: Color
        color2: Color
        transform: Matrix4
    }

    //TODO: these aren't really display related, they should be separated

    export const RenderSettings = defSceneNode<RenderSettings>("RenderSettings")

    export interface RenderSettings extends SceneNodeBase {
        readonly type: "RenderSettings"
        templateDepth: number
        percentage: number
        samples: number
        gpu: boolean
        cloud: boolean
        textureStreaming: boolean
        renderTiling: "off" | "on" | "auto"
        denoiseTiling: "off" | "on" | "auto"
    }

    export const RenderPostProcessingSettings = defSceneNode<RenderPostProcessingSettings>("RenderPostProcessingSettings")

    export interface RenderPostProcessingSettings extends SceneNodeBase {
        readonly type: "RenderPostProcessingSettings"
        templateDepth: number
        mode: "whiteBackground"
        exposure: number
        whiteBalance?: number
        toneMapping?: ToneMapping
        lutUrl?: string
        transparent?: boolean
        composite?: boolean
        backgroundColor?: Color | DataObject
        mask?: DataObject
        processShadows?: boolean
        shadowInner?: number
        shadowOuter?: number
        shadowFalloff?: number
        shadowOpacity?: number
        autoCrop?: boolean
        autoCropMargin?: number
    }

    export type SceneNode =
        | Mesh
        | MeshCurveControl
        | Seam
        | DimensionGuides
        | WireframeMesh
        | Rectangle
        | Cuboid
        | Point
        | SceneOptions
        | Environment
        | Camera
        | AreaLight
        | PointLight
        | SunLight
        | LightPortal
        | Annotation
        | PreloadMaterial
        | DataObjectReference
        | Grid
        | RenderSettings
        | RenderPostProcessingSettings
}

export type IDisplaySceneEvent = {
    readonly type: string
}

export interface IDisplayView {
    getDOMElement(): HTMLElement

    findObjectsAtPoint(x: number, y: number): [ObjectId, number][]

    surfaceInfoAtPoint(x: number, y: number, filterFn?: (ids: ObjectId[]) => ObjectId | null | undefined): SurfacePointInfo | null
}

export const SceneNodeSchema = z.custom<SceneNodes.SceneNode>((x) => x !== null && typeof x === "object" && SceneNodes.isSceneNode(x as object))

export const SceneNodeMeshSchema = z.custom<SceneNodes.Mesh>((x) => SceneNodes.Mesh.is(x))
